/**
 * Audible and haptic cues for the active liveness challenge.
 *
 * The head-turn challenge has a blind spot: once the user turns away, the screen is
 * out of view, so an on-screen instruction can no longer tell them when the turn has
 * been held long enough and it is time to look back. Without a non-visual signal the
 * user has to guess, and a mistimed return fails the challenge.
 *
 * Tones are synthesised through the Web Audio API rather than shipped as audio files —
 * no assets, no network fetch, and the cue fires the instant the phase changes.
 * Vibration runs alongside for phones, where the device is usually held at arm's length
 * and a short buzz reads more reliably than a tone in a noisy room.
 *
 * Everything here fails silently: audio may be blocked before a user gesture, vibration
 * is unsupported on desktop and iOS Safari. A missing cue degrades the experience but
 * must never interrupt the verification.
 */

let audioContext: AudioContext | null = null;

/** Lazily create the shared AudioContext. Must be called after a user gesture. */
function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  try {
    if (!audioContext) {
      const Ctor = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctor) return null;
      audioContext = new Ctor();
    }
    // Browsers suspend the context until a gesture; resuming is a no-op when running
    if (audioContext.state === 'suspended') void audioContext.resume();
    return audioContext;
  } catch {
    return null;
  }
}

/**
 * Prime the audio context from within a user gesture handler.
 * Call this when the user starts the capture, so later cues are allowed to play.
 */
export function primeAudioCues(): void {
  getContext();
}

interface ToneSpec {
  frequency: number;
  durationMs: number;
  /** Delay before this tone starts, for multi-tone sequences. */
  offsetMs?: number;
  volume?: number;
}

function playTones(tones: ToneSpec[]): void {
  const ctx = getContext();
  if (!ctx) return;

  for (const tone of tones) {
    try {
      const startAt = ctx.currentTime + (tone.offsetMs ?? 0) / 1000;
      const endAt = startAt + tone.durationMs / 1000;

      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = tone.frequency;

      // Short fade in/out — a hard start or stop produces an audible click
      const peak = tone.volume ?? 0.22;
      gain.gain.setValueAtTime(0.0001, startAt);
      gain.gain.exponentialRampToValueAtTime(peak, startAt + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, endAt);

      oscillator.connect(gain);
      gain.connect(ctx.destination);
      oscillator.start(startAt);
      oscillator.stop(endAt + 0.02);
    } catch {
      // A failed tone must not break the challenge
    }
  }
}

function vibrate(pattern: number | number[]): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(pattern);
    }
  } catch {
    // Unsupported or blocked — ignore
  }
}

/**
 * Cue: start turning your head.
 * Single mid tone — informational, the screen is still visible at this point.
 */
export function cueTurnStart(): void {
  playTones([{ frequency: 660, durationMs: 160 }]);
  vibrate(60);
}

/**
 * Cue: the turn was held long enough — look back at the screen now.
 *
 * This is the important one. Two rising tones, louder and longer than the others,
 * so it carries while the user is facing away from the device.
 */
export function cueLookBack(): void {
  playTones([
    { frequency: 880, durationMs: 180, volume: 0.3 },
    { frequency: 1170, durationMs: 260, offsetMs: 190, volume: 0.3 },
  ]);
  vibrate([90, 70, 160]);
}

/** Cue: holding still, the photo is being taken. */
export function cueCapturing(): void {
  playTones([{ frequency: 990, durationMs: 130 }]);
  vibrate(50);
}

/** Cue: challenge passed. Two ascending tones. */
export function cueSuccess(): void {
  playTones([
    { frequency: 880, durationMs: 140 },
    { frequency: 1320, durationMs: 300, offsetMs: 150 },
  ]);
  vibrate([60, 60, 120]);
}

/** Cue: challenge failed. Two descending tones — distinct from success. */
export function cueFailure(): void {
  playTones([
    { frequency: 520, durationMs: 180 },
    { frequency: 340, durationMs: 320, offsetMs: 190 },
  ]);
  vibrate([200, 100, 200]);
}
